import { call, cancel, fork, put, take, takeLatest } from 'redux-saga/effects';
import {
  getBasePackagesApi,
  getCustomPackagesApi,
  createPackageApi,
  updatePackageApi,
  deletePackageApi,
  getTerritoriesApi,
  getLeadSourcesApi,
  getBonusSubscriptionTypesApi,
  getBillingPackageLogs,
} from 'api/billingPackage';
import { errorMessage, successMessage } from 'state/toast/actions';
import {
  GET_BASE_PACKAGES,
  GET_CUSTOM_PACKAGES,
  SAVE_PACKAGE,
  DELETE_PACKAGE,
  GET_TERRITORIES,
  GET_LEAD_SOURCES,
  GET_BONUS_SUBSCRIPTION_TYPES,
  BILLING_PACKAGE_DO_SHOW_LOG,
  BILLING_PACKAGE_LOG_CHANGE_PAGE,
  BILLING_PACKAGE_LOG_SET_SHOWN,
} from './actionTypes';
import {
  updateBasePackages,
  updateCustomPackages,
  setPackagesLoading,
  setPackageEditorLoading,
  setPackageEditorError,
  showCreatePackageModal,
  showEditPackageModal,
  showDeleteConfirmation,
  setDeleteLoading,
  getBasePackages,
  getCustomPackages,
  updateTerritories,
  updateLeadSources,
  updateBonusSubscriptionTypes,
  setLogData,
  setLogIsLoading,
  setLogErrors,
  clearLogErrors,
  resetLogData,
  setLogModalShown,
  setLogPage,
} from './actions';

export function* handleGetBasePackages() {
  try {
    yield put(setPackagesLoading(true));
    const response = yield call(getBasePackagesApi);
    yield put(updateBasePackages(response.data.data || []));
  } catch (error) {
    yield put(errorMessage('Error', 'Failed to load base packages'));
  } finally {
    yield put(setPackagesLoading(false));
  }
}

export function* handleGetCustomPackages() {
  try {
    yield put(setPackagesLoading(true));
    const response = yield call(getCustomPackagesApi);
    yield put(updateCustomPackages(response.data.data || []));
  } catch (error) {
    yield put(errorMessage('Error', 'Failed to load custom packages'));
  } finally {
    yield put(setPackagesLoading(false));
  }
}

export function* handleSavePackage(action) {
  const { packagePayload, isEditMode } = action.payload;

  try {
    yield put(setPackageEditorLoading(true));
    yield put(setPackageEditorError(null));

    if (isEditMode) {
      yield call(updatePackageApi, packagePayload);
    } else {
      yield call(createPackageApi, packagePayload);
    }

    yield put(showCreatePackageModal(false));
    yield put(showEditPackageModal(false, null, false));

    yield put(
      successMessage(
        'Success',
        `Package ${isEditMode ? 'updated' : 'created'} successfully`
      )
    );

    yield put(getBasePackages());
    yield put(getCustomPackages());
  } catch (error) {
    const data = error.response && error.response.data;
    const errorMsg =
      (data && data.message) ||
      `Failed to ${isEditMode ? 'update' : 'create'} package`;
    const backendErrors = (data && data.errors) || [];
    yield put(setPackageEditorError(errorMsg, backendErrors));
  } finally {
    yield put(setPackageEditorLoading(false));
  }
}

export function* handleDeletePackage(action) {
  const { packageId } = action.payload;

  try {
    yield put(setDeleteLoading(true));
    yield call(deletePackageApi, packageId);

    yield put(showDeleteConfirmation(false, null, false));

    yield put(successMessage('Success', 'Package deleted successfully'));

    yield put(getBasePackages());
    yield put(getCustomPackages());
  } catch (error) {
    yield put(
      errorMessage(
        'Error',
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
          'Failed to delete package'
      )
    );
  } finally {
    yield put(setDeleteLoading(false));
  }
}

export function* handleGetTerritories() {
  try {
    const response = yield call(getTerritoriesApi);
    const territories =
      response.data.data &&
      response.data.data.map(territory => ({
        value: territory.id,
        label: territory.name,
      }));
    yield put(updateTerritories(territories || []));
  } catch (error) {
    yield put(updateTerritories([]));
  }
}

export function* handleGetLeadSources() {
  try {
    const response = yield call(getLeadSourcesApi);
    yield put(updateLeadSources(response.data.data || []));
  } catch (error) {
    yield put(updateLeadSources([]));
  }
}

export function* handleGetBonusSubscriptionTypes() {
  try {
    const response = yield call(getBonusSubscriptionTypesApi);
    yield put(updateBonusSubscriptionTypes(response.data.data || []));
  } catch (error) {
    yield put(updateBonusSubscriptionTypes([]));
  }
}

function* logModalTask() {
  while (true) {
    const { page } = (yield take(BILLING_PACKAGE_LOG_CHANGE_PAGE)).payload;
    yield put(setLogIsLoading(true));
    yield put(clearLogErrors());
    yield put(resetLogData());
    try {
      const {
        data: {
          data: { internal_tools_activities: activities },
          paging,
        },
      } = yield call(getBillingPackageLogs, page, 20);
      yield put(setLogData(activities, paging));
    } catch (e) {
      yield put(setLogErrors(e.message || ['Failed to load logs']));
    } finally {
      yield put(setLogIsLoading(false));
    }
  }
}

export function* doShowLogs() {
  const task = yield fork(logModalTask);
  yield put(setLogPage(1));
  yield put(setLogModalShown(true));
  let closeModalRequest;
  do {
    closeModalRequest = (yield take(BILLING_PACKAGE_LOG_SET_SHOWN)).payload;
  } while (closeModalRequest.shown);
  yield cancel(task);
}

export default function* billingPackageSaga() {
  yield takeLatest(GET_BASE_PACKAGES, handleGetBasePackages);
  yield takeLatest(GET_CUSTOM_PACKAGES, handleGetCustomPackages);
  yield takeLatest(SAVE_PACKAGE, handleSavePackage);
  yield takeLatest(DELETE_PACKAGE, handleDeletePackage);
  yield takeLatest(GET_TERRITORIES, handleGetTerritories);
  yield takeLatest(GET_LEAD_SOURCES, handleGetLeadSources);
  yield takeLatest(
    GET_BONUS_SUBSCRIPTION_TYPES,
    handleGetBonusSubscriptionTypes
  );
  yield takeLatest(BILLING_PACKAGE_DO_SHOW_LOG, doShowLogs);
}
